import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Cookie } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useSiteSettings } from '../context/SiteSettingsContext';

const COOKIE_CONSENT_KEY = 'cookie_consent_accepted';

export default function CookieBanner() {
  const [isVisible, setIsVisible] = useState(false);
  const { language } = useLanguage();
  const { settings } = useSiteSettings();
  
  // Check if dark mode based on settings
  const isDarkMode = settings?.design_dark_mode === true;
  
  useEffect(() => {
    // Check if user has already accepted cookies
    const hasAccepted = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!hasAccepted) {
      // Small delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);
  
  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    setIsVisible(false);
  };
  
  const handleClose = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    setIsVisible(false);
  };
  
  if (!isVisible) return null;
  
  // Translations
  const texts = {
    de: {
      title: 'Cookie-Hinweis',
      message: 'Wir verwenden Cookies, um Ihnen die beste Erfahrung auf unserer Website zu bieten.',
      privacy: 'Datenschutz',
      accept: 'Akzeptieren'
    },
    en: {
      title: 'Cookie Notice',
      message: 'We use cookies to provide you with the best experience on our website.',
      privacy: 'Privacy Policy',
      accept: 'Accept'
    }
  };
  
  const t = texts[language] || texts.de;
  
  return (
    <div 
      className={`fixed bottom-4 right-4 z-50 max-w-sm rounded-lg shadow-2xl border transition-all duration-300 transform ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-4 opacity-0'
      } ${
        isDarkMode 
          ? 'bg-[#1a1a1a] border-[#333] text-white' 
          : 'bg-white border-[#E5E5E5] text-[#0A0A0A]'
      }`}
      data-testid="cookie-banner"
    >
      {/* Close button */}
      <button
        onClick={handleClose}
        className={`absolute top-2 right-2 p-1 rounded-full transition-colors ${
          isDarkMode 
            ? 'hover:bg-[#333] text-[#999]' 
            : 'hover:bg-[#f5f5f5] text-[#666]'
        }`}
        aria-label="Close"
        data-testid="cookie-banner-close"
      >
        <X size={16} />
      </button>
      
      <div className="p-4 pr-8">
        {/* Header with icon */}
        <div className="flex items-center gap-2 mb-2">
          <Cookie size={18} className={isDarkMode ? 'text-[#C9A227]' : 'text-[#C9A227]'} />
          <h4 className="font-heading text-sm font-medium">{t.title}</h4>
        </div>
        
        {/* Message */}
        <p className={`text-xs mb-3 ${isDarkMode ? 'text-[#999]' : 'text-[#666]'}`}>
          {t.message}{' '}
          <Link 
            to="/datenschutz" 
            className="underline hover:no-underline"
            style={{ color: settings?.design_primary_color || '#C9A227' }}
          >
            {t.privacy}
          </Link>
        </p>
        
        {/* Accept button */}
        <button
          onClick={handleAccept}
          className="w-full py-2 px-4 text-xs font-medium tracking-wider transition-colors rounded"
          style={{ 
            backgroundColor: settings?.design_primary_color || '#C9A227',
            color: isDarkMode ? '#0A0A0A' : '#0A0A0A'
          }}
          data-testid="cookie-banner-accept"
        >
          {t.accept.toUpperCase()}
        </button>
      </div>
    </div>
  );
}
